import React, { useState, useMemo } from 'react';
import { useTable, useSortBy, useFilters, usePagination } from 'react-table';
import Swal from 'sweetalert2';
import { User, Calendar, DollarSign, FileText, Plus, Edit, Trash2, Download, Eye } from 'lucide-react';

// Datos de ejemplo
const initialEmployees = [
  {
    id: 1,
    nombre: 'Juan Pérez',
    cedula: '1234567890',
    cargo: 'Técnico de Calibración',
    email: 'juan.perez@calibracion.com',
    telefono: '3001234567',
    fechaIngreso: '2020-01-15',
    salarioBase: 2500000,
    departamento: 'Operaciones',
    eps: 'Sanitas',
    afp: 'Porvenir',
    arl: 'Sura',
    estadoCivil: 'Casado',
    direccion: 'Calle 123 #45-67, Bogotá',
    eventos: [
      { id: 1, tipo: 'Capacitación', fecha: '2024-01-10', descripcion: 'Curso de Metrología Avanzada', afectaNomina: false },
      { id: 2, tipo: 'Vacaciones', fecha: '2024-07-15', dias: 10, descripcion: 'Vacaciones programadas', afectaNomina: true }
    ]
  },
  {
    id: 2,
    nombre: 'María González',
    cedula: '9876543210',
    cargo: 'Ingeniera de Calidad',
    email: 'maria.gonzalez@calibracion.com',
    telefono: '3109876543',
    fechaIngreso: '2019-03-20',
    salarioBase: 3800000,
    departamento: 'Calidad',
    eps: 'Compensar',
    afp: 'Protección',
    arl: 'Positiva',
    estadoCivil: 'Soltera',
    direccion: 'Carrera 7 #89-12, Bogotá',
    eventos: [
      { id: 3, tipo: 'Seminario', fecha: '2024-02-20', descripcion: 'ISO 17025:2017', afectaNomina: false },
      { id: 4, tipo: 'Incapacidad', fecha: '2024-08-05', dias: 3, descripcion: 'Incapacidad médica general', afectaNomina: true }
    ]
  }
];

const PerfilPersonal = () => {
  const [employees, setEmployees] = useState(initialEmployees);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeView, setActiveView] = useState('list');
  const [mesNomina, setMesNomina] = useState(new Date().getMonth() + 1);
  const [anioNomina, setAnioNomina] = useState(new Date().getFullYear());

  // Componente de Lista de Empleados
  const EmployeeTable = () => {
    const columns = useMemo(() => [
      { Header: 'Nombre', accessor: 'nombre' },
      { Header: 'Cédula', accessor: 'cedula' },
      { Header: 'Cargo', accessor: 'cargo' },
      { Header: 'Departamento', accessor: 'departamento' },
      { 
        Header: 'Salario Base', 
        accessor: 'salarioBase',
        Cell: ({ value }) => `$${value.toLocaleString('es-CO')}`
      },
      {
        Header: 'Acciones',
        Cell: ({ row }) => (
          <div className="btn-group" role="group">
            <button className="btn btn-sm btn-info" onClick={() => viewEmployee(row.original)}>
              <Eye size={14} />
            </button>
            <button className="btn btn-sm btn-warning" onClick={() => editEmployee(row.original)}>
              <Edit size={14} />
            </button>
            <button className="btn btn-sm btn-danger" onClick={() => deleteEmployee(row.original.id)}>
              <Trash2 size={14} />
            </button>
          </div>
        )
      }
    ], []);

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      page,
      prepareRow,
      canPreviousPage,
      canNextPage,
      pageOptions,
      nextPage,
      previousPage,
      state: { pageIndex }
    } = useTable(
      { columns, data: employees, initialState: { pageSize: 5 } },
      useFilters,
      useSortBy,
      usePagination
    );

return (
        <div className='card w-100'>
            <div className="card-header w-100 bg-primary text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0 d-flex align-items-center">
                <User size={20} className="me-2" /> Lista de Empleados
            </h5>
            <button className="btn btn-light btn-sm" onClick={addEmployee}>
                <Plus size={16} /> Nuevo Empleado
            </button>
            </div>
            <div className="card-body p-3 w-100" style={{ overflowX: 'auto' }}>
            <div className="table-responsive w-100">
                <table
                {...getTableProps()}
                className="table table-striped table-hover align-middle mb-0 w-100"
                >
                <thead className="table-dark">
                    {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                        <th
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            className="text-nowrap"
                        >
                            {column.render('Header')}
                            <span>
                            {column.isSorted
                                ? column.isSortedDesc
                                ? ' 🔽'
                                : ' 🔼'
                                : ''}
                            </span>
                        </th>
                        ))}
                    </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map(row => {
                    prepareRow(row);
                    return (
                        <tr {...row.getRowProps()}>
                        {row.cells.map(cell => (
                            <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                        ))}
                        </tr>
                    );
                    })}
                </tbody>
                </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                <div>
                Página <strong>{pageIndex + 1}</strong> de <strong>{pageOptions.length}</strong>
                </div>
                <div className="btn-group mt-2 mt-sm-0">
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                >
                    Anterior
                </button>
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                >
                    Siguiente
                </button>
                </div>
            </div>
            </div>
        </div>
        );
  };

  // Componente de Perfil del Empleado
  const EmployeeProfile = ({ employee }) => {
    if (!employee) return null;

    const addEvent = async () => {
      const { value: formValues } = await Swal.fire({
        title: 'Registrar Evento',
        html: `
          <select id="tipo" class="swal2-input">
            <option value="Capacitación">Capacitación</option>
            <option value="Permiso">Permiso</option>
            <option value="Incapacidad">Incapacidad</option>
            <option value="Seminario">Seminario</option>
            <option value="Vacaciones">Vacaciones</option>
            <option value="Llamado de Atención">Llamado de Atención</option>
          </select>
          <input type="date" id="fecha" class="swal2-input" placeholder="Fecha">
          <input type="number" id="dias" class="swal2-input" placeholder="Días (si aplica)">
          <textarea id="descripcion" class="swal2-textarea" placeholder="Descripción"></textarea>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Guardar',
        cancelButtonText: 'Cancelar',
        preConfirm: () => {
          return {
            tipo: document.getElementById('tipo').value,
            fecha: document.getElementById('fecha').value,
            dias: parseInt(document.getElementById('dias').value) || 0,
            descripcion: document.getElementById('descripcion').value
          };
        }
      });

      if (formValues) {
        const afectaNomina = ['Vacaciones', 'Incapacidad', 'Permiso'].includes(formValues.tipo);
        const newEvent = {
          id: Date.now(),
          ...formValues,
          afectaNomina
        };

        setEmployees(prev => prev.map(emp => 
          emp.id === employee.id 
            ? { ...emp, eventos: [...emp.eventos, newEvent] }
            : emp
        ));

        Swal.fire('¡Registrado!', 'El evento ha sido registrado exitosamente.', 'success');
      }
    };

    const deleteEvent = (eventId) => {
      Swal.fire({
        title: '¿Estás seguro?',
        text: "Esta acción no se puede deshacer",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          setEmployees(prev => prev.map(emp => 
            emp.id === employee.id 
              ? { ...emp, eventos: emp.eventos.filter(e => e.id !== eventId) }
              : emp
          ));
          Swal.fire('Eliminado', 'El evento ha sido eliminado.', 'success');
        }
      });
    };

    return (
      <div className="row">
        <div className="col-lg-4">
          <div className="card mb-3">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0"><User size={20} /> Información Personal</h5>
            </div>
            <div className="card-body">
              <p><strong>Nombre:</strong> {employee.nombre}</p>
              <p><strong>Cédula:</strong> {employee.cedula}</p>
              <p><strong>Cargo:</strong> {employee.cargo}</p>
              <p><strong>Departamento:</strong> {employee.departamento}</p>
              <p><strong>Email:</strong> {employee.email}</p>
              <p><strong>Teléfono:</strong> {employee.telefono}</p>
              <p><strong>Estado Civil:</strong> {employee.estadoCivil}</p>
              <p><strong>Dirección:</strong> {employee.direccion}</p>
              <p><strong>Fecha Ingreso:</strong> {new Date(employee.fechaIngreso).toLocaleDateString('es-CO')}</p>
            </div>
          </div>

          <div className="card">
            <div className="card-header bg-success text-white">
              <h5 className="mb-0"><DollarSign size={20} /> Información Salarial</h5>
            </div>
            <div className="card-body">
              <p><strong>Salario Base:</strong> ${employee.salarioBase.toLocaleString('es-CO')}</p>
              <p><strong>EPS:</strong> {employee.eps}</p>
              <p><strong>AFP:</strong> {employee.afp}</p>
              <p><strong>ARL:</strong> {employee.arl}</p>
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="card">
            <div className="card-header bg-warning d-flex justify-content-between align-items-center">
              <h5 className="mb-0"><Calendar size={20} /> Historial de Eventos</h5>
              <button className="btn btn-dark btn-sm" onClick={addEvent}>
                <Plus size={16} /> Nuevo Evento
              </button>
            </div>
            <div className="card-body">
              <div className="list-group">
                {employee.eventos.map(evento => (
                  <div key={evento.id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="mb-1">
                          <span className={`badge ${evento.afectaNomina ? 'bg-danger' : 'bg-info'} me-2`}>
                            {evento.tipo}
                          </span>
                          {evento.descripcion}
                        </h6>
                        <p className="mb-1 text-muted">
                          <small>Fecha: {new Date(evento.fecha).toLocaleDateString('es-CO')}</small>
                          {evento.dias > 0 && <small className="ms-3">Días: {evento.dias}</small>}
                        </p>
                      </div>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => deleteEvent(evento.id)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
                {employee.eventos.length === 0 && (
                  <p className="text-muted text-center py-4">No hay eventos registrados</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Componente de Nómina
  const PayrollComponent = () => {
    const calcularNomina = (empleado, mes, anio) => {
      const salarioBase = empleado.salarioBase;
      const diasMes = 30;
      const salarioDiario = salarioBase / diasMes;

      // Filtrar eventos del mes que afectan nómina
      const eventosDelMes = empleado.eventos.filter(e => {
        const fechaEvento = new Date(e.fecha);
        return fechaEvento.getMonth() + 1 === mes && 
               fechaEvento.getFullYear() === anio &&
               e.afectaNomina;
      });

      let diasDescuento = 0;
      let detallesDescuentos = [];

      eventosDelMes.forEach(evento => {
        if (evento.tipo === 'Vacaciones') {
          // Las vacaciones en Colombia se pagan
          detallesDescuentos.push(`${evento.tipo}: ${evento.dias} días (pagados)`);
        } else if (evento.tipo === 'Incapacidad') {
          // Los primeros 2 días los paga el empleador, después la EPS
          const diasEmpleador = Math.min(evento.dias, 2);
          const diasEPS = Math.max(evento.dias - 2, 0);
          if (diasEPS > 0) {
            diasDescuento += diasEPS;
            detallesDescuentos.push(`${evento.tipo}: ${diasEPS} días cubiertos por EPS`);
          }
        } else if (evento.tipo === 'Permiso') {
          diasDescuento += evento.dias;
          detallesDescuentos.push(`${evento.tipo}: ${evento.dias} días`);
        }
      });

      // Deducciones de ley Colombia
      const saludEmpleado = salarioBase * 0.04; // 4% Salud
      const pensionEmpleado = salarioBase * 0.04; // 4% Pensión
      const descuentoDias = salarioDiario * diasDescuento;
      
      const totalDeducciones = saludEmpleado + pensionEmpleado + descuentoDias;
      const salarioNeto = salarioBase - totalDeducciones;

      // Aportes patronales (informativo)
      const saludPatronal = salarioBase * 0.085; // 8.5%
      const pensionPatronal = salarioBase * 0.12; // 12%
      const arlPatronal = salarioBase * 0.00522; // 0.522% (riesgo 1)
      const cajaBancajaPatronal = salarioBase * 0.04; // 4%

      return {
        salarioBase,
        deducciones: {
          salud: saludEmpleado,
          pension: pensionEmpleado,
          diasDescuento: descuentoDias,
          detalles: detallesDescuentos
        },
        salarioNeto,
        aportes: {
          salud: saludPatronal,
          pension: pensionPatronal,
          arl: arlPatronal,
          caja: cajaBancajaPatronal
        },
        totalAportesPatronales: saludPatronal + pensionPatronal + arlPatronal + cajaBancajaPatronal
      };
    };

    const nominaData = employees.map(emp => ({
      ...emp,
      nomina: calcularNomina(emp, mesNomina, anioNomina)
    }));

    const columns = useMemo(() => [
      { Header: 'Empleado', accessor: 'nombre' },
      { Header: 'Cargo', accessor: 'cargo' },
      { 
        Header: 'Salario Base', 
        accessor: 'salarioBase',
        Cell: ({ value }) => `$${value.toLocaleString('es-CO')}`
      },
      { 
        Header: 'Deducciones', 
        accessor: 'nomina',
        Cell: ({ value }) => `$${(value.deducciones.salud + value.deducciones.pension + value.deducciones.diasDescuento).toLocaleString('es-CO')}`
      },
      { 
        Header: 'Salario Neto', 
        accessor: row => row.nomina.salarioNeto,
        Cell: ({ value }) => <strong>${value.toLocaleString('es-CO')}</strong>
      },
      {
        Header: 'Detalle',
        Cell: ({ row }) => (
          <button className="btn btn-sm btn-primary" onClick={() => verDetalleNomina(row.original)}>
            <Eye size={14} /> Ver
          </button>
        )
      }
    ], [mesNomina, anioNomina]);

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow
    } = useTable({ columns, data: nominaData });

    const verDetalleNomina = (empleado) => {
      const nomina = empleado.nomina;
      Swal.fire({
        title: `Nómina - ${empleado.nombre}`,
        html: `
          <div class="text-start">
            <h6>Devengado</h6>
            <p>Salario Base: $${nomina.salarioBase.toLocaleString('es-CO')}</p>
            
            <h6 class="mt-3">Deducciones</h6>
            <p>Salud (4%): $${nomina.deducciones.salud.toLocaleString('es-CO')}</p>
            <p>Pensión (4%): $${nomina.deducciones.pension.toLocaleString('es-CO')}</p>
            ${nomina.deducciones.diasDescuento > 0 ? 
              `<p>Descuentos por días: $${nomina.deducciones.diasDescuento.toLocaleString('es-CO')}</p>
              ${nomina.deducciones.detalles.map(d => `<small class="text-muted">- ${d}</small><br>`).join('')}` 
              : ''}
            <p><strong>Total Deducciones: $${(nomina.deducciones.salud + nomina.deducciones.pension + nomina.deducciones.diasDescuento).toLocaleString('es-CO')}</strong></p>
            
            <h6 class="mt-3">Neto a Pagar</h6>
            <p class="fs-5"><strong>$${nomina.salarioNeto.toLocaleString('es-CO')}</strong></p>
            
            <hr>
            <h6>Aportes Patronales (informativo)</h6>
            <small>Salud (8.5%): $${nomina.aportes.salud.toLocaleString('es-CO')}</small><br>
            <small>Pensión (12%): $${nomina.aportes.pension.toLocaleString('es-CO')}</small><br>
            <small>ARL: $${nomina.aportes.arl.toLocaleString('es-CO')}</small><br>
            <small>Caja Compensación (4%): $${nomina.aportes.caja.toLocaleString('es-CO')}</small><br>
            <small><strong>Total Patronal: $${nomina.totalAportesPatronales.toLocaleString('es-CO')}</strong></small>
          </div>
        `,
        width: 600,
        confirmButtonText: 'Cerrar'
      });
    };

    const exportarNomina = () => {
      Swal.fire({
        icon: 'success',
        title: 'Exportar Nómina',
        text: `La funcionalidad de exportación a Excel estará disponible próximamente`,
        confirmButtonText: 'Entendido'
      });
    };

    const totalNeto = nominaData.reduce((sum, emp) => sum + emp.nomina.salarioNeto, 0);

    return (
      <div className="card w-100">
        <div className="card-header w-100 bg-success text-white">
          <div className="row align-items-center">
            <div className="col">
              <h5 className="mb-0"><DollarSign size={20} /> Nómina Mensual</h5>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm d-inline-block w-auto me-2" 
                      value={mesNomina} 
                      onChange={(e) => setMesNomina(parseInt(e.target.value))}>
                <option value={1}>Enero</option>
                <option value={2}>Febrero</option>
                <option value={3}>Marzo</option>
                <option value={4}>Abril</option>
                <option value={5}>Mayo</option>
                <option value={6}>Junio</option>
                <option value={7}>Julio</option>
                <option value={8}>Agosto</option>
                <option value={9}>Septiembre</option>
                <option value={10}>Octubre</option>
                <option value={11}>Noviembre</option>
                <option value={12}>Diciembre</option>
              </select>
              <input type="number" className="form-control form-control-sm d-inline-block w-auto me-2" 
                     value={anioNomina} 
                     onChange={(e) => setAnioNomina(parseInt(e.target.value))}
                     min="2020" max="2030" />
              <button className="btn btn-light btn-sm" onClick={exportarNomina}>
                <Download size={16} /> Exportar
              </button>
            </div>
          </div>
        </div>
        <div className="card-body w-100">
          <div className="table-responsive w-100">
            <table {...getTableProps()} className="table table-striped">
              <thead className="table-dark">
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="table-info">
                  <td colSpan="4" className="text-end"><strong>Total a Pagar:</strong></td>
                  <td colSpan="2"><strong>${totalNeto.toLocaleString('es-CO')}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Funciones auxiliares
  const viewEmployee = (employee) => {
    setSelectedEmployee(employee);
    setActiveView('profile');
  };

  const addEmployee = async () => {
    const { value: formValues } = await Swal.fire({
      title: 'Nuevo Empleado',
      html: `
        <input type="text" id="nombre" class="swal2-input" placeholder="Nombre completo">
        <input type="text" id="cedula" class="swal2-input" placeholder="Cédula">
        <input type="text" id="cargo" class="swal2-input" placeholder="Cargo">
        <input type="email" id="email" class="swal2-input" placeholder="Email">
        <input type="text" id="telefono" class="swal2-input" placeholder="Teléfono">
        <input type="number" id="salario" class="swal2-input" placeholder="Salario Base">
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        return {
          nombre: document.getElementById('nombre').value,
          cedula: document.getElementById('cedula').value,
          cargo: document.getElementById('cargo').value,
          email: document.getElementById('email').value,
          telefono: document.getElementById('telefono').value,
          salarioBase: parseInt(document.getElementById('salario').value)
        };
      }
    });

    if (formValues) {
      const newEmployee = {
        id: Date.now(),
        ...formValues,
        fechaIngreso: new Date().toISOString().split('T')[0],
        departamento: 'Operaciones',
        eps: 'Sanitas',
        afp: 'Porvenir',
        arl: 'Sura',
        estadoCivil: 'Soltero',
        direccion: 'Por definir',
        eventos: []
      };
      setEmployees([...employees, newEmployee]);
      Swal.fire('¡Guardado!', 'El empleado ha sido creado exitosamente.', 'success');
    }
  };

  const editEmployee = async (employee) => {
    Swal.fire('Información', 'Funcionalidad de edición en desarrollo', 'info');
  };

  const deleteEmployee = (id) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "Esta acción no se puede deshacer",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        setEmployees(employees.filter(emp => emp.id !== id));
        Swal.fire('Eliminado', 'El empleado ha sido eliminado.', 'success');
      }
    });
  };

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div className="mb-4">
        <h1 className="display-6"><FileText size={32} /> Sistema de Gestión de Personal</h1>
        <p className="text-muted">Empresa de Calibración - Colombia</p>
      </div>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button className={`nav-link ${activeView === 'list' ? 'active' : ''}`} 
                  onClick={() => setActiveView('list')}>
            <User size={16} /> Empleados
          </button>
        </li>
        {selectedEmployee && (
          <li className="nav-item">
            <button className={`nav-link ${activeView === 'profile' ? 'active' : ''}`} 
                    onClick={() => setActiveView('profile')}>
              <FileText size={16} /> Perfil: {selectedEmployee.nombre}
            </button>
          </li>
        )}
        <li className="nav-item">
          <button className={`nav-link ${activeView === 'payroll' ? 'active' : ''}`} 
                  onClick={() => setActiveView('payroll')}>
            <DollarSign size={16} /> Nómina
          </button>
        </li>
      </ul>

      {activeView === 'list' && <EmployeeTable />}
      {activeView === 'profile' && <EmployeeProfile employee={selectedEmployee} />}
      {activeView === 'payroll' && <PayrollComponent />}
    </div>
  );
};

export default PerfilPersonal;